import React, { useState, useCallback, useRef } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, RefreshControl, Switch } from 'react-native';
import { useRouter, useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useOwnerScope } from '../../../src/context/OwnerScopeContext';
import { Alert } from 'react-native';
import { confirmDestructive } from '../../../src/utils/confirm';
import Card from '../../../src/components/Card';
import Button from '../../../src/components/Button';
import ContentContainer from '../../../src/components/ContentContainer';
import { useToast } from '../../../src/components/Toast';
import { useTheme } from '../../../src/context/ThemeContext';
import { useNetwork } from '../../../src/context/NetworkContext';
import type { ColorTheme } from '../../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';
import { rs, fs } from '../../../src/utils/responsive';
import { titleCase } from '../../../src/utils/format';
import { AdminToolbar } from '../../../src/components/AdminList';

interface SubscriptionPlan {
  id: string;
  name: string;
  description: string | null;
  planType?: 'automatic' | 'self_serve' | 'mixed';
  tierName: string | null;
  priceMonthly: number;
  maxWashesPerDay: number | null;
  maxWashesPerMonth: number | null;
  maxMinutesPerDay: number | null;
  maxMinutesPerMonth: number | null;
  locationScope: string;
  locationName: string | null;
  isActive: boolean;
  subscriberCount: number;
}

interface MrrData {
  mrr: number;
  activeCount: number;
  canceledCount: number;
  washesThisMonth: number;
  automaticWashesThisMonth?: number;
  selfServeSessionsThisMonth?: number;
}

interface AnalyticsData {
  mrr: number;
  activeCount: number;
  canceledCount: number;
  byPlanType: Record<'automatic' | 'self_serve' | 'mixed', { mrr: number; activeCount: number; canceledCount: number }>;
  churnRate: number;
  canceledLast30: number;
  activeAt30dAgo: number;
  ltvByPlanType: Record<'automatic' | 'self_serve' | 'mixed', number>;
  automaticWashesThisMonth: number;
  selfServeMinutesThisMonth: number;
  selfServeSessionsThisMonth: number;
}

interface BundlePlan {
  id: string;
  name: string;
  description: string | null;
  tierName: string;
  washCount: number;
  bundlePrice: number;
  locationScope: string;
  locationName: string | null;
  expirationDays: number | null;
  isActive: boolean;
  purchaserCount: number;
}

interface BundleRevenue {
  totalRevenueThisMonth: number;
  activeBundleCount: number;
  washesThisMonth: number;
  totalBundlesSold: number;
}

interface VoucherRow {
  id: string;
  code: string;
  kind: 'gift' | 'promo' | 'points_redemption';
  discountKind: 'percent' | 'free_wash' | 'free_minutes' | 'free_vend' | 'credit' | 'free_upsell';
  percentOff: number | null;
  tierName: string | null;
  freeMinutes?: number | null;
  freeVendCents?: number | null;
  creditCents?: number | null;
  /** Human label for multi-select applicability rows ("Ultimate, Deluxe + Self-serve + Vacuums + Vending"); null on legacy rows. */
  appliesToLabel?: string | null;
  recipientEmail: string | null;
  status: 'pending' | 'active' | 'redeemed' | 'expired' | 'revoked';
  redemptionsUsed: number;
  redemptionLimit: number;
  claimsCount: number;
  issuedByAttendantName?: string | null;
  source: string;
  expiresAt: string | null;
  createdAt: string;
  scanCount: number;
  lastScanAt: string | null;
}

/** Offer headline for a voucher row — appliesToLabel is server-rendered and
 *  already includes Vacuums/Vending; never re-derive scope client-side. */
function voucherOffer(v: VoucherRow): string {
  if (v.discountKind === 'credit') {
    return `$${((v.creditCents ?? 0) / 100).toFixed(2)} credit (deposited at claim)`;
  }
  if (v.discountKind === 'free_vend') {
    return `$${((v.freeVendCents ?? 0) / 100).toFixed(2)} vending credit`;
  }
  if (v.discountKind === 'free_upsell') return 'Free add-on with a wash';
  if (v.discountKind === 'free_minutes') {
    const base = `${v.freeMinutes ?? '?'} free minutes`;
    return v.appliesToLabel ? `${base} · ${v.appliesToLabel}` : base;
  }
  if (v.discountKind === 'free_wash' || (v.discountKind === 'percent' && v.percentOff === 100)) {
    return v.appliesToLabel ? `Free wash · ${v.appliesToLabel}` : `Free ${v.tierName || 'wash'}`;
  }
  return v.appliesToLabel
    ? `${v.percentOff}% off · ${v.appliesToLabel}`
    : `${v.percentOff}% off${v.tierName ? ` ${v.tierName}` : ''}`;
}

/** SubscriptionPlansScreen - membership plans and wash bundles overview. */
export default function SubscriptionPlansScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);

  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [mrr, setMrr] = useState<MrrData | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  /** Owner opt-in for member plan switching (null until loaded). */
  const [allowPlanChanges, setAllowPlanChanges] = useState<boolean | null>(null);
  const [savingPlanChanges, setSavingPlanChanges] = useState(false);
  const [bundlePlans, setBundlePlans] = useState<BundlePlan[]>([]);
  const [bundleRevenue, setBundleRevenue] = useState<BundleRevenue | null>(null);
  const [loading, setLoading] = useState(true);
  const [qrRows, setQrRows] = useState<VoucherRow[]>([]);
  const [qrTotal, setQrTotal] = useState(0);
  const [qrPage, setQrPage] = useState(1);
  const [qrSearch, setQrSearch] = useState('');
  const [qrFilter, setQrFilter] = useState('');
  const qrSearchRef = useRef('');
  const [emRows, setEmRows] = useState<VoucherRow[]>([]);
  const [emTotal, setEmTotal] = useState(0);
  const [emPage, setEmPage] = useState(1);
  const [emSearch, setEmSearch] = useState('');
  const [emFilter, setEmFilter] = useState('');
  const emSearchRef = useRef('');
  const [voucherStats, setVoucherStats] = useState<{ total: number; active: number; redeemed: number; expired: number } | null>(null);
  const router = useRouter();
  const toast = useToast();
  const { isOffline } = useNetwork();
  const scope = useOwnerScope();

  /** 'closed' filter chip maps to both terminal hidden statuses. */
  const voucherParams = (source: 'qr' | 'emailed', page: number, search: string, filter: string) => {
    const params = new URLSearchParams({ page: String(page), limit: '10', source });
    if (search.trim()) params.set('search', search.trim());
    if (filter === 'closed') params.set('status', 'expired,revoked');
    else if (filter) params.set('status', filter);
    return params;
  };

  const fetchQrVouchers = async (p = 1, f = qrFilter) => {
    try {
      const data = await scope.api.get<{ vouchers: VoucherRow[]; total: number; stats: { total: number; active: number; redeemed: number; expired: number } }>(
        `/api/vouchers?${voucherParams('qr', p, qrSearchRef.current, f)}`);
      setQrRows(data.vouchers);
      setQrTotal(data.total);
      setQrPage(p);
      if (data.stats) setVoucherStats(data.stats);
    } catch { /* section keeps prior rows; pull-to-refresh retries */ }
  };

  const fetchEmailedVouchers = async (p = 1, f = emFilter) => {
    try {
      const data = await scope.api.get<{ vouchers: VoucherRow[]; total: number }>(
        `/api/vouchers?${voucherParams('emailed', p, emSearchRef.current, f)}`);
      setEmRows(data.vouchers);
      setEmTotal(data.total);
      setEmPage(p);
    } catch { /* section keeps prior rows; pull-to-refresh retries */ }
  };

  /** CSV exports every matching row (incl. hidden when unfiltered), not just the page. */
  const voucherCsvEndpoint = (source: 'qr' | 'emailed', search: string, filter: string) => {
    const params = voucherParams(source, 1, search, filter);
    params.delete('page');
    params.delete('limit');
    if (!filter) params.set('includeHidden', '1');
    if (scope.ownerId) params.set('ownerId', scope.ownerId);
    return `/api/vouchers?${params}`;
  };

  const fmtDT = (d: string) => new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

  const fetchData = async () => {
    let failures = 0;
    const track = <T,>(p: Promise<T>, fallback: T): Promise<T> => p.catch(() => { failures++; return fallback; });
    try {
      setLoading(true);
      const [planData, mrrData, analyticsData, bundleData, bundleRevData, planChangeData] = await Promise.all([   // order must match the array below
        scope.api.get<SubscriptionPlan[]>('/api/subscription-plans'),
        scope.api.get<MrrData>('/api/subscriptions/mrr'),
        track(scope.api.get<AnalyticsData | null>('/api/subscriptions/analytics'), null),
        scope.api.get<BundlePlan[]>('/api/bundle-plans'),
        scope.api.get<BundleRevenue>('/api/bundles/revenue'),
        track(scope.api.get<{ allowPlanChanges: boolean } | null>('/api/owner/plan-change-setting'), null),
        fetchQrVouchers(1),
        fetchEmailedVouchers(1),
      ]);
      setPlans(planData);
      setMrr(mrrData);
      setAnalytics(analyticsData);
      setBundlePlans(bundleData);
      setBundleRevenue(bundleRevData);
      if (planChangeData) setAllowPlanChanges(planChangeData.allowPlanChanges);
      if (failures > 0 && !isOffline) {
        toast.show({ type: 'warning', title: 'Some data didn’t load', message: `${failures} section${failures === 1 ? '' : 's'} failed to refresh.` });
      }
    } catch (err: any) {
      if (!isOffline) {
        toast.show({ type: 'error', title: 'Error', message: err.message });
      }
    } finally {
      setLoading(false);
    }
  };

  useFocusEffect(useCallback(() => { fetchData(); }, []));

  const fmt = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const togglePlanChanges = async (value: boolean) => {
    setSavingPlanChanges(true);
    const prev = allowPlanChanges;
    setAllowPlanChanges(value);
    try {
      await scope.api.patch('/api/owner/plan-change-setting', { allowPlanChanges: value });
      toast.show({
        type: 'success',
        title: value ? 'Plan switching enabled' : 'Plan switching disabled',
        message: value ? 'Members can now upgrade instantly (prorated) or downgrade at renewal.' : undefined,
      });
    } catch (err: any) {
      setAllowPlanChanges(prev);
      if (!isOffline) toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setSavingPlanChanges(false);
    }
  };

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.content}
      refreshControl={<RefreshControl refreshing={loading} onRefresh={fetchData} />}
    >
      <ContentContainer>
      <Button
        title="Wash Tiers & Upsells"
        onPress={() => router.push(scope.basePath + '/pricing/washes-upsells')}
        icon={<Ionicons name="pricetags-outline" size={20} color={colors.primary} />}
        variant="outline"
        style={{ marginBottom: spacing.md }}
      />

      {/* === SUBSCRIPTIONS === */}
      <Text style={styles.sectionTitle}>Subscriptions</Text>
      {mrr && (
        <Card style={styles.mrrCard}>
          <Text style={styles.mrrLabel}>Monthly Recurring Revenue</Text>
          <Text style={styles.mrrAmount}>{fmt(mrr.mrr)}</Text>
          <View style={styles.mrrStats}>
            <View style={styles.mrrStat}>
              <Text style={styles.mrrStatValue}>{mrr.activeCount}</Text>
              <Text style={styles.mrrStatLabel}>Active</Text>
            </View>
            <View style={styles.mrrStat}>
              <Text style={styles.mrrStatValue}>{mrr.canceledCount}</Text>
              <Text style={styles.mrrStatLabel}>Canceled</Text>
            </View>
            <View style={styles.mrrStat}>
              <Text style={styles.mrrStatValue}>{mrr.washesThisMonth}</Text>
              <Text style={styles.mrrStatLabel}>Washes</Text>
              <Text style={styles.mrrStatSub}>{mrr.automaticWashesThisMonth ?? 0} auto · {mrr.selfServeSessionsThisMonth ?? 0} self</Text>
            </View>
            <View style={styles.mrrStat}>
              <Text style={styles.mrrStatValue}>{mrr.activeCount > 0 ? fmt(Math.round(mrr.mrr / mrr.activeCount)) : '—'}</Text>
              <Text style={styles.mrrStatLabel}>Rev/Member</Text>
            </View>
          </View>
        </Card>
      )}

      {/* Member plan switching (mig 0052): upgrades now + prorated, downgrades at renewal. */}
      {allowPlanChanges !== null && (
        <Card style={{ marginBottom: spacing.md }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.text }}>
                Let members switch plans
              </Text>
              <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginTop: 2 }}>
                Upgrades apply instantly with a prorated charge; downgrades start at the next billing date.
              </Text>
            </View>
            <Switch
              value={allowPlanChanges}
              onValueChange={togglePlanChanges}
              disabled={savingPlanChanges}
              trackColor={{ true: colors.primary }}
            />
          </View>
        </Card>
      )}

      {analytics && (analytics.byPlanType.self_serve.activeCount > 0 || analytics.byPlanType.mixed.activeCount > 0 || analytics.activeAt30dAgo > 0) && (
        <Card style={styles.analyticsCard}>
          <View style={styles.analyticsRow}>
            <View style={styles.analyticsStat}>
              <Text style={styles.analyticsValue}>{(analytics.churnRate * 100).toFixed(1)}%</Text>
              <Text style={styles.analyticsLabel}>30-day churn</Text>
            </View>
            <View style={styles.analyticsStat}>
              <Text style={styles.analyticsValue}>{fmt(analytics.ltvByPlanType.automatic + analytics.ltvByPlanType.self_serve + analytics.ltvByPlanType.mixed)}</Text>
              <Text style={styles.analyticsLabel}>Avg LTV</Text>
            </View>
            <View style={styles.analyticsStat}>
              <Text style={styles.analyticsValue}>{Math.round(analytics.selfServeMinutesThisMonth)}</Text>
              <Text style={styles.analyticsLabel}>Self-serve min</Text>
            </View>
            <View style={styles.analyticsStat}>
              <Text style={styles.analyticsValue}>{analytics.selfServeSessionsThisMonth}</Text>
              <Text style={styles.analyticsLabel}>Self-serve sess</Text>
            </View>
          </View>
        </Card>
      )}

      <Button
        title="Create New Plan"
        onPress={() => router.push(scope.basePath + '/subscriptions/add-plan')}
        icon={<Ionicons name="add" size={20} color={colors.primary} />}
        variant="outline"
        style={{ marginBottom: spacing.md }}
      />

      {/* Plans List */}
      {plans.length === 0 && !loading && (
        <Card style={styles.emptyCard}>
          <Ionicons name="ribbon-outline" size={48} color={colors.textLight} />
          <Text style={styles.emptyTitle}>No Subscription Plans</Text>
          <Text style={styles.emptyText}>Create your first membership plan to start earning recurring revenue.</Text>
        </Card>
      )}

      {plans.map(plan => {
        const planType = plan.planType ?? 'automatic';
        const includesAuto = planType === 'automatic' || planType === 'mixed';
        const includesSelf = planType === 'self_serve' || planType === 'mixed';
        const subtitle = planType === 'self_serve'
          ? 'Self-serve plan'
          : planType === 'mixed'
            ? `Mixed · ${plan.tierName && /^[1-4]$/.test(plan.tierName) ? `Option ${plan.tierName}` : plan.tierName ?? ''}`
            : (plan.tierName && /^[1-4]$/.test(plan.tierName) ? `Option ${plan.tierName}` : plan.tierName ?? '');
        return (
          <TouchableOpacity
            key={plan.id}
            onPress={() => router.push(scope.ownerId ? `${scope.basePath}/subscriptions/plan/${plan.id}` : `/owner/subscriptions/${plan.id}`)}
          >
            <Card style={[styles.planCard, !plan.isActive && styles.planCardInactive]}>
              <View style={styles.planHeader}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.planName}>{plan.name}</Text>
                  <Text style={styles.planTier}>{subtitle}</Text>
                </View>
                <View style={styles.planPrice}>
                  <Text style={styles.planPriceAmount}>{fmt(plan.priceMonthly)}</Text>
                  <Text style={styles.planPricePeriod}>/month</Text>
                </View>
              </View>
              <View style={styles.planDetails}>
                <View style={styles.planDetail}>
                  <Ionicons name="people" size={14} color={colors.textSecondary} />
                  <Text style={styles.planDetailText}>{plan.subscriberCount} subscriber{plan.subscriberCount !== 1 ? 's' : ''}</Text>
                </View>
                {includesAuto && (
                  <View style={styles.planDetail}>
                    <Ionicons name="car" size={14} color={colors.textSecondary} />
                    <Text style={styles.planDetailText}>{plan.maxWashesPerDay === 0 ? 'Unlimited/day' : `${plan.maxWashesPerDay}/day`}, {plan.maxWashesPerMonth === 0 ? 'unlimited/mo' : `${plan.maxWashesPerMonth}/mo`}</Text>
                  </View>
                )}
                {includesSelf && (
                  <View style={styles.planDetail}>
                    <Ionicons name="water" size={14} color={colors.textSecondary} />
                    <Text style={styles.planDetailText}>{plan.maxMinutesPerDay === 0 ? 'Unlimited min/day' : `${plan.maxMinutesPerDay} min/day`}, {plan.maxMinutesPerMonth === 0 ? 'unlimited min/mo' : `${plan.maxMinutesPerMonth} min/mo`}</Text>
                  </View>
                )}
                <View style={styles.planDetail}>
                  <Ionicons name="location" size={14} color={colors.textSecondary} />
                  <Text style={styles.planDetailText}>
                    {plan.locationScope === 'all' ? 'All locations' : plan.locationName || 'Single location'}
                  </Text>
                </View>
                {!plan.isActive && (
                  <View style={[styles.planDetail, { backgroundColor: colors.danger + '15', paddingHorizontal: 8, borderRadius: 4 }]}>
                    <Text style={[styles.planDetailText, { color: colors.danger }]}>Inactive</Text>
                  </View>
                )}
              </View>
            </Card>
          </TouchableOpacity>
        );
      })}

      {/* === WASH BUNDLES === */}
      <Text style={styles.sectionTitle}>Wash Bundles</Text>
      {bundleRevenue && bundleRevenue.totalBundlesSold > 0 && (
        <Card style={[styles.mrrCard, { backgroundColor: colors.success + 'dd' }]}>
          <Text style={styles.mrrLabel}>Bundle Revenue (This Month)</Text>
          <Text style={styles.mrrAmount}>{fmt(bundleRevenue.totalRevenueThisMonth)}</Text>
          <View style={styles.mrrStats}>
            <View style={styles.mrrStat}>
              <Text style={styles.mrrStatValue}>{bundleRevenue.activeBundleCount}</Text>
              <Text style={styles.mrrStatLabel}>Active</Text>
            </View>
            <View style={styles.mrrStat}>
              <Text style={styles.mrrStatValue}>{bundleRevenue.totalBundlesSold}</Text>
              <Text style={styles.mrrStatLabel}>Total Sold</Text>
            </View>
            <View style={styles.mrrStat}>
              <Text style={styles.mrrStatValue}>{bundleRevenue.washesThisMonth}</Text>
              <Text style={styles.mrrStatLabel}>Washes</Text>
            </View>
          </View>
        </Card>
      )}
      <Button
        title="Create New Bundle"
        onPress={() => router.push(scope.basePath + '/subscriptions/add-bundle')}
        icon={<Ionicons name="cube-outline" size={20} color={colors.primary} />}
        variant="outline"
        style={{ marginBottom: spacing.md }}
      />
      {bundlePlans.length > 0 && (
        <>
          {bundlePlans.map(bp => (
            <TouchableOpacity
              key={bp.id}
              onPress={() => router.push(`${scope.basePath}/subscriptions/bundle/${bp.id}`)}
            >
              <Card style={[styles.planCard, !bp.isActive && styles.planCardInactive]}>
                <View style={styles.planHeader}>
                  <View style={{ flex: 1 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
                      <Ionicons name="cube" size={16} color={colors.primary} />
                      <Text style={styles.planName}>{bp.name}</Text>
                    </View>
                    <Text style={styles.planTier}>{/^[1-4]$/.test(bp.tierName) ? `Option ${bp.tierName}` : bp.tierName} — {bp.washCount} washes</Text>
                  </View>
                  <View style={styles.planPrice}>
                    <Text style={styles.planPriceAmount}>{fmt(bp.bundlePrice)}</Text>
                    <Text style={styles.planPricePeriod}>one-time</Text>
                  </View>
                </View>
                <View style={styles.planDetails}>
                  <View style={styles.planDetail}>
                    <Ionicons name="people" size={14} color={colors.textSecondary} />
                    <Text style={styles.planDetailText}>{bp.purchaserCount} active</Text>
                  </View>
                  <View style={styles.planDetail}>
                    <Ionicons name="location" size={14} color={colors.textSecondary} />
                    <Text style={styles.planDetailText}>
                      {bp.locationScope === 'all' ? 'All locations' : bp.locationName || 'Single location'}
                    </Text>
                  </View>
                  {bp.expirationDays && (
                    <View style={styles.planDetail}>
                      <Ionicons name="time" size={14} color={colors.textSecondary} />
                      <Text style={styles.planDetailText}>{bp.expirationDays} day expiry</Text>
                    </View>
                  )}
                  {!bp.isActive && (
                    <View style={[styles.planDetail, { backgroundColor: colors.danger + '15', paddingHorizontal: 8, borderRadius: 4 }]}>
                      <Text style={[styles.planDetailText, { color: colors.danger }]}>Inactive</Text>
                    </View>
                  )}
                </View>
              </Card>
            </TouchableOpacity>
          ))}
        </>
      )}

      {/* Gifts & Promos summary card */}
      <Text style={styles.sectionTitle}>Gifts & Promos</Text>
      <Card style={[styles.mrrCard, { backgroundColor: colors.primaryDark }]}>
        <Text style={styles.mrrLabel}>Vouchers issued</Text>
        <Text style={styles.mrrAmount}>{voucherStats?.total ?? 0}</Text>
        <View style={styles.mrrStats}>
          <View style={styles.mrrStat}>
            <Text style={styles.mrrStatValue}>{voucherStats?.active ?? 0}</Text>
            <Text style={styles.mrrStatLabel}>Active</Text>
          </View>
          <View style={styles.mrrStat}>
            <Text style={styles.mrrStatValue}>{voucherStats?.redeemed ?? 0}</Text>
            <Text style={styles.mrrStatLabel}>Redeemed</Text>
          </View>
          <View style={styles.mrrStat}>
            <Text style={styles.mrrStatValue}>{voucherStats?.expired ?? 0}</Text>
            <Text style={styles.mrrStatLabel}>Expired/Closed</Text>
          </View>
        </View>
      </Card>

      {/* All program-action buttons grouped together (no section headers between).
          Theme matches "Create New Bundle" — outline variant with primary-color icons. */}
      {/* One combined form covers free wash / % off / free minutes / $ credit
          (absorbed the former "Issue Gift % Off" + "Issue Gift Credit" pages). */}
      <Button
        title="Issue Wash / Credit"
        icon={<Ionicons name="gift" size={20} color={colors.primary} />}
        variant="outline"
        onPress={() => router.push(scope.ownerId ? `${scope.basePath}/programs/voucher-new` : '/owner/subscriptions/gifts/new')}
        style={{ marginBottom: spacing.sm }}
      />
      <Button
        title="Generate Promo QR codes"
        icon={<Ionicons name="qr-code" size={20} color={colors.primary} />}
        variant="outline"
        onPress={() => router.push(scope.ownerId ? `${scope.basePath}/programs/qr-new` : '/owner/subscriptions/qr-codes/new')}
        style={{ marginBottom: spacing.sm }}
      />
      <Button
        title="Lapsed Customer Outreach"
        icon={<Ionicons name="paper-plane" size={20} color={colors.primary} />}
        variant="outline"
        onPress={() => router.push(scope.ownerId ? `${scope.basePath}/programs/winback` : '/owner/subscriptions/winback')}
        style={{ marginBottom: spacing.sm }}
      />
      <Button
        title="Configure Points Program"
        icon={<Ionicons name="star" size={20} color={colors.primary} />}
        variant="outline"
        onPress={() => router.push(scope.ownerId ? `${scope.basePath}/programs/points-edit` : '/owner/subscriptions/points-settings')}
        style={{ marginBottom: spacing.md }}
      />

      {/* Active programs list (after the buttons, per user direction) */}
      <Text style={styles.sectionTitle}>Programs</Text>

      {/* QR programs first — full cards, tap-to-detail */}
      <Text style={styles.subSectionTitle}>QR Programs</Text>
      <AdminToolbar
        search={qrSearch}
        onSearchChange={s => { qrSearchRef.current = s; setQrSearch(s); }}
        onSearch={() => fetchQrVouchers(1)}
        total={qrTotal}
        page={qrPage}
        limit={10}
        onPageChange={p => fetchQrVouchers(p)}
        csvEndpoint={voucherCsvEndpoint('qr', qrSearch, qrFilter)}
        csvFilename="qr-programs.csv"
        label="QR program"
        filterOptions={[
          { label: 'Active', value: 'active' },
          { label: 'Fully Redeemed', value: 'redeemed' },
          { label: 'Expired/Closed', value: 'closed' },
        ]}
        activeFilter={qrFilter}
        onFilterChange={f => { setQrFilter(f); fetchQrVouchers(1, f); }}
      />
      {qrRows.length === 0 && (
        <Text style={styles.hintText}>
          {qrSearch || qrFilter ? 'No QR programs match.' : 'No QR programs yet. Expired and closed QRs are hidden — use the Expired/Closed filter to see them.'}
        </Text>
      )}
      {qrRows.map(v => {
        const canDelete = v.status === 'active' || v.status === 'pending';
        const wasUsed = v.redemptionsUsed > 0;
        const handleDelete = () => {
          const proceed = async () => {
            try {
              await scope.api.api(`/api/vouchers/${v.id}`, { method: 'DELETE' });
              toast.show({ type: 'success', title: wasUsed ? 'Closed' : 'Revoked' });
              fetchQrVouchers(qrPage);
            } catch (err: any) {
              toast.show({ type: 'error', title: 'Error', message: err.message });
            }
          };
          if (wasUsed) {
            // Backend soft-closes: no NEW claims; customers who already
            // claimed keep their discount.
            confirmDestructive(
              'Close this voucher?',
              'No one new will be able to claim it. Customers who already claimed keep their discount.',
              proceed,
            );
            return;
          }
          confirmDestructive(
            'Delete this voucher?',
            'The redemption code will stop working immediately. This cannot be undone.',
            proceed,
          );
        };
        return (
          <TouchableOpacity
            key={v.id}
            onPress={() => router.push(`${scope.basePath}/subscriptions/qr-codes/${v.id}`)}
            activeOpacity={0.7}
          >
            <Card style={[styles.planCard, (v.status !== 'active') && styles.planCardInactive]}>
              <View style={styles.planHeader}>
                <View style={{ flex: 1 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
                    <Ionicons name="qr-code" size={16} color={colors.primary} />
                    <Text style={styles.planName}>{v.source === 'gift_qr_single' ? 'Single-use QR' : 'Multi-use QR'}</Text>
                  </View>
                  <Text style={styles.planTier}>{voucherOffer(v)}</Text>
                </View>
                <View style={[styles.planPrice, { alignItems: 'flex-end' }]}>
                  <Text style={[styles.planPriceAmount, { fontSize: fontSize.sm, color: v.status === 'active' ? colors.success : colors.textSecondary }]}>
                    {v.status}
                  </Text>
                  <Text style={styles.planPricePeriod}>{v.code}</Text>
                </View>
              </View>
              <View style={styles.planDetails}>
                <View style={styles.planDetail}>
                  <Ionicons name="calendar" size={14} color={colors.textSecondary} />
                  <Text style={styles.planDetailText}>Created {fmtDT(v.createdAt)}</Text>
                </View>
                <View style={styles.planDetail}>
                  <Ionicons name="eye" size={14} color={colors.textSecondary} />
                  <Text style={styles.planDetailText}>
                    {v.scanCount > 0 ? `Scanned ${v.scanCount}× · last ${fmtDT(v.lastScanAt!)}` : 'Never scanned'}
                  </Text>
                </View>
                <View style={styles.planDetail}>
                  <Ionicons name="ticket" size={14} color={colors.textSecondary} />
                  <Text style={styles.planDetailText}>{v.redemptionsUsed}/{v.redemptionLimit} used</Text>
                </View>
                <TouchableOpacity
                  onPress={() => router.push(`${scope.basePath}/subscriptions/vouchers/${v.id}`)}
                  style={[styles.planDetail, { backgroundColor: colors.primary + '12', paddingHorizontal: 8, borderRadius: 4 }]}
                >
                  <Ionicons name="people" size={14} color={colors.primary} />
                  <Text style={[styles.planDetailText, { color: colors.primary }]}>{v.claimsCount} claimed</Text>
                </TouchableOpacity>
                {v.issuedByAttendantName && (
                  <View style={styles.planDetail}>
                    <Ionicons name="person" size={14} color={colors.textSecondary} />
                    <Text style={styles.planDetailText}>by {titleCase(v.issuedByAttendantName)}</Text>
                  </View>
                )}
                {v.expiresAt && (
                  <View style={styles.planDetail}>
                    <Ionicons name="time" size={14} color={colors.textSecondary} />
                    <Text style={styles.planDetailText}>Expires {fmtDT(v.expiresAt)}</Text>
                  </View>
                )}
                {canDelete && v.redemptionsUsed < v.redemptionLimit && (
                  <TouchableOpacity onPress={handleDelete} style={[styles.planDetail, { backgroundColor: colors.danger + '15', paddingHorizontal: 8, borderRadius: 4 }]}>
                    <Ionicons name="trash" size={14} color={colors.danger} />
                    <Text style={[styles.planDetailText, { color: colors.danger }]}>Delete</Text>
                  </TouchableOpacity>
                )}
                {wasUsed && (
                  <View style={[styles.planDetail, { backgroundColor: colors.warning + '15', paddingHorizontal: 8, borderRadius: 4 }]}>
                    <Ionicons name="information-circle" size={14} color={colors.warning} />
                    <Text style={[styles.planDetailText, { color: colors.warning }]}>can't delete</Text>
                  </View>
                )}
              </View>
            </Card>
          </TouchableOpacity>
        );
      })}

      {/* Emailed gifts/promos — compact searchable list, server-paginated */}
      <Text style={[styles.subSectionTitle, { marginTop: spacing.md }]}>Emailed Gifts & Promos</Text>
      <AdminToolbar
        search={emSearch}
        onSearchChange={s => { emSearchRef.current = s; setEmSearch(s); }}
        onSearch={() => fetchEmailedVouchers(1)}
        total={emTotal}
        page={emPage}
        limit={10}
        onPageChange={p => fetchEmailedVouchers(p)}
        csvEndpoint={voucherCsvEndpoint('emailed', emSearch, emFilter)}
        csvFilename="gifts-promos.csv"
        label="gift/promo"
        filterOptions={[
          { label: 'Active', value: 'active' },
          { label: 'Fully Redeemed', value: 'redeemed' },
          { label: 'Expired/Closed', value: 'closed' },
        ]}
        activeFilter={emFilter}
        onFilterChange={f => { setEmFilter(f); fetchEmailedVouchers(1, f); }}
      />
      {emRows.length === 0 ? (
        <Text style={styles.hintText}>
          {emSearch || emFilter ? 'No vouchers match your search.' : 'No emailed gifts or promos yet.'}
        </Text>
      ) : (
        <Card style={{ paddingVertical: 0 }}>
          {emRows.map((v, idx) => {
            const canDelete = v.status === 'active' || v.status === 'pending';
            const wasUsed = v.redemptionsUsed > 0;
            const handleDelete = () => {
              const proceed = async () => {
                try {
                  await scope.api.api(`/api/vouchers/${v.id}`, { method: 'DELETE' });
                  toast.show({ type: 'success', title: wasUsed ? 'Closed' : 'Revoked' });
                  fetchEmailedVouchers(emPage);
                } catch (err: any) {
                  toast.show({ type: 'error', title: 'Error', message: err.message });
                }
              };
              if (wasUsed) {
                // Backend soft-closes: no NEW claims; existing claim-holders keep theirs.
                confirmDestructive(
                  'Close this voucher?',
                  'No one new will be able to claim it. Customers who already claimed keep their discount.',
                  proceed,
                );
                return;
              }
              confirmDestructive(
                'Delete this voucher?',
                'The redemption code will stop working immediately. This cannot be undone.',
                proceed,
              );
            };
            const offer = voucherOffer(v);
            return (
              <View key={v.id} style={[styles.emailRow, idx === emRows.length - 1 && { borderBottomWidth: 0 }]}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.emailAddr} numberOfLines={1}>{v.recipientEmail ?? '(no email)'}</Text>
                  <Text style={styles.emailSub} numberOfLines={1}>
                    {offer} · {v.code} · <Text style={{ color: v.status === 'active' ? colors.success : colors.textSecondary }}>{v.status}</Text>
                  </Text>
                  <Text style={styles.emailSub} numberOfLines={1}>Sent {fmtDT(v.createdAt)}</Text>
                </View>
                <TouchableOpacity
                  onPress={() => router.push(`${scope.basePath}/subscriptions/vouchers/${v.id}`)}
                  hitSlop={8}
                  style={{ flexDirection: 'row', alignItems: 'center', gap: 2, paddingHorizontal: spacing.xs, paddingVertical: spacing.xs }}
                >
                  <Ionicons name="people" size={14} color={colors.primary} />
                  <Text style={{ color: colors.primary, fontSize: fontSize.xs, fontWeight: fontWeight.semibold as any }}>{v.claimsCount}</Text>
                  <Ionicons name="chevron-forward" size={14} color={colors.textLight} />
                </TouchableOpacity>
                {canDelete && v.redemptionsUsed < v.redemptionLimit && (
                  <TouchableOpacity onPress={handleDelete} hitSlop={8} style={{ padding: spacing.xs }}>
                    <Ionicons name="trash" size={16} color={colors.danger} />
                  </TouchableOpacity>
                )}
              </View>
            );
          })}
        </Card>
      )}

      <Button
        title="View and/or Import Customers"
        icon={<Ionicons name="people" size={20} color={colors.primary} />}
        variant="outline"
        onPress={() => router.push(scope.basePath + '/customers')}
        style={{ marginTop: spacing.md }}
      />

      <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },

  analyticsCard: { padding: spacing.md, marginBottom: spacing.md, backgroundColor: c.surface, borderRadius: borderRadius.lg, borderWidth: 1, borderColor: c.borderLight },
  analyticsRow: { flexDirection: 'row', justifyContent: 'space-around' },
  analyticsStat: { alignItems: 'center', flex: 1 },
  analyticsValue: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.text },
  analyticsLabel: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  mrrCard: { alignItems: 'center', padding: spacing.lg, marginBottom: spacing.md, backgroundColor: c.primaryDark, borderRadius: borderRadius.lg },
  mrrLabel: { fontSize: fontSize.sm, color: 'rgba(255,255,255,0.8)' },
  mrrAmount: { fontSize: fs(36), fontWeight: fontWeight.bold, color: c.white, marginTop: spacing.xs },
  mrrStats: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: spacing.xl, rowGap: spacing.sm, marginTop: spacing.md },
  mrrStat: { alignItems: 'center' },
  mrrStatValue: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.white },
  mrrStatLabel: { fontSize: fontSize.xs, color: 'rgba(255,255,255,0.7)' },
  mrrStatSub: { fontSize: 9, color: 'rgba(255,255,255,0.55)', marginTop: 1 },

  emptyCard: { alignItems: 'center', padding: spacing.xl },
  emptyTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginTop: spacing.md },
  emptyText: { fontSize: fontSize.sm, color: c.textSecondary, textAlign: 'center', marginTop: spacing.sm, maxWidth: 280 },

  planCard: { marginBottom: spacing.sm },
  planCardInactive: { opacity: 0.6 },
  planHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: spacing.sm },
  planName: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text },
  planTier: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  planPrice: { alignItems: 'flex-end' },
  planPriceAmount: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.primary },
  planPricePeriod: { fontSize: fontSize.xs, color: c.textSecondary },

  planDetails: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  planDetail: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  planDetailText: { fontSize: fontSize.xs, color: c.textSecondary },

  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginTop: spacing.md, marginBottom: spacing.sm },
  subSectionTitle: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.textSecondary, marginTop: spacing.sm, marginBottom: spacing.xs, textTransform: 'uppercase', letterSpacing: 0.5 },
  exportBtn: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, paddingHorizontal: spacing.sm, paddingVertical: spacing.xs, borderRadius: borderRadius.sm, borderWidth: 1, borderColor: c.primary },
  searchRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, backgroundColor: c.surface, borderRadius: borderRadius.md, paddingHorizontal: spacing.md, paddingVertical: spacing.sm, marginBottom: spacing.sm, borderWidth: 1, borderColor: c.borderLight },
  searchInput: { flex: 1, color: c.text, fontSize: fontSize.sm, padding: 0 },
  hintText: { fontSize: fontSize.sm, color: c.textLight, fontStyle: 'italic', textAlign: 'center', padding: spacing.sm },
  emailRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: c.borderLight },
  emailAddr: { fontSize: fontSize.sm, color: c.text, fontWeight: fontWeight.semibold },
  emailSub: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
